/**
 * Collection endless: grid banners + featured copy blocks
 *
 * Loaded only on pages rendering `sections/collection-endless.liquid`.
 * Works in the Shopify theme editor by being:
 * - idempotent (safe to run multiple times)
 * - compatible with section re-rendering (new DOM nodes get upgraded automatically)
 * - resilient to dynamic grid updates (MutationObserver)
 */
(function () {
	if (!window.customElements) return;

	const GRID_SELECTOR = "#product-grid";
	const MOBILE_WIDTH_PX = 600; // Keep existing banner breakpoint behavior.

	let isRepositioning = false;
	let bannersScheduled = false;

	function getProductGrid(root = document) {
		return root.querySelector(GRID_SELECTOR);
	}

	function isMobileBannerLayout() {
		return window.innerWidth < MOBILE_WIDTH_PX;
	}

	function getMobileCols(productGrid) {
		if (productGrid) {
			const classes = productGrid.className;
			const match = classes.match(/wt-grid--cols-(\d+)/);
			if (match) return parseInt(match[1], 10) || 2;
		}
		return 2;
	}

	function getProductCards(productGrid) {
		const allItems = productGrid.querySelectorAll('li[data-item-number]');
		return Array.from(allItems).filter((item) => {
			// Exclude info blocks (they contain productitem--info-block).
			return !item.querySelector(".productitem--info-block");
		});
	}

	function findProductCardByHandle(products, handle) {
		if (!handle) return null;
		return products.find((li) => (li.dataset.productHandle || "") === handle) || null;
	}

	function findProductCardById(products, id) {
		if (!id) return null;
		const idStr = String(id);
		return products.find((li) => (li.dataset.productId || "") === idStr) || null;
	}

	function computeAnchor(products, bannerEl, productGrid) {
		// New placement: insert after a chosen product (desktop).
		const afterProductHandle = (bannerEl.dataset.afterProductHandle || "").trim();
		const afterProductId = (bannerEl.dataset.afterProductId || "").trim();

		const mobileInsertMode = (bannerEl.dataset.mobileInsertMode || "same_as_desktop").trim();
		const mobileAfterRow = parseInt(bannerEl.dataset.mobileAfterRow, 10) || 0;

		function snapAnchorToMobileRowEnd(anchorProduct) {
			if (!isMobileBannerLayout()) return anchorProduct;
			if (!productGrid) return anchorProduct;

			const cols = getMobileCols(productGrid);
			if (!cols || cols <= 1) return anchorProduct;

			const idx = products.indexOf(anchorProduct);
			if (idx < 0) return anchorProduct;

			// Insert after the row containing the selected product, so we never split a row
			// (prevents the empty right-side gap in 2-col mobile grids).
			const oneBased = idx + 1;
			const row = Math.ceil(oneBased / cols);
			const rowEndOneBased = Math.min(row * cols, products.length);
			return products[rowEndOneBased - 1] || anchorProduct;
		}

		// Mobile override: after row.
		if (isMobileBannerLayout() && mobileInsertMode === "after_row") {
			const productsPerRow = getMobileCols(productGrid);
			const afterRow = mobileAfterRow;
			if (!afterRow || afterRow <= 0) return null;
			const lastProductOfTargetRow = Math.min(afterRow * productsPerRow, products.length);
			if (products.length < lastProductOfTargetRow) return null;
			return products[lastProductOfTargetRow - 1] || null;
		}

		// Default: after selected product (desktop + mobile).
		const byHandle = findProductCardByHandle(products, afterProductHandle);
		if (byHandle) return snapAnchorToMobileRowEnd(byHandle);

		const byId = findProductCardById(products, afterProductId);
		if (byId) return snapAnchorToMobileRowEnd(byId);

		// If the selected product isn't in the rendered grid (filters/sort/pagination),
		// we don't have a reliable anchor — hide the banner.
		return null;
	}

	function scheduleBannerPlacement(root = document) {
		if (bannersScheduled) return;
		bannersScheduled = true;
		window.requestAnimationFrame(() => {
			bannersScheduled = false;
			placeAllBanners(root);
		});
	}

	function placeAllBanners(root = document) {
		const scope = root || document;
		const productGrid = getProductGrid(scope) || getProductGrid(document);
		if (!productGrid) return;

		const banners = Array.from(scope.querySelectorAll("collection-grid-banner"));
		if (!banners.length) return;

		const products = getProductCards(productGrid);
		if (!products.length) return;

		isRepositioning = true;
		try {
			// Build groups: same anchor => stable insertion order (DOM order of banners).
			const groups = new Map();

			for (const bannerEl of banners) {
				const bannerContent = bannerEl.querySelector("li");
				if (!bannerContent) continue;

				const anchor = computeAnchor(products, bannerEl, productGrid);
				if (!anchor) {
					bannerContent.style.display = "none";
					continue;
				}
				bannerContent.style.display = "";

				const key = anchor.dataset.itemNumber
					? `after-item-${anchor.dataset.itemNumber}`
					: `after-node-${products.indexOf(anchor)}`;

				if (!groups.has(key)) groups.set(key, { anchor, items: [] });
				groups.get(key).items.push({ bannerEl, bannerContent });
			}

			// Insert groups in DOM order of their anchor within the product list.
			const orderedGroups = Array.from(groups.values()).sort((a, b) => {
				const ai = products.indexOf(a.anchor);
				const bi = products.indexOf(b.anchor);
				return ai - bi;
			});

			for (const group of orderedGroups) {
				let insertionPoint = group.anchor.nextSibling;

				// If there are already banners right after the anchor (from a previous run),
				// advance insertionPoint past them so we preserve stable grouping.
				while (
					insertionPoint &&
					insertionPoint.nodeType === Node.ELEMENT_NODE &&
					insertionPoint.tagName === "LI" &&
					insertionPoint.querySelector(".productitem--info-block")
				) {
					// Defensive: shouldn’t happen because info blocks are banners themselves.
					insertionPoint = insertionPoint.nextSibling;
				}

				// Insert banners in their original banner DOM order.
				for (const { bannerContent } of group.items) {
					// If it’s already exactly at insertionPoint, skip move.
					if (
						bannerContent.parentNode === productGrid &&
						(bannerContent === insertionPoint || bannerContent.nextSibling === insertionPoint)
					) {
						insertionPoint = bannerContent.nextSibling;
						continue;
					}

					productGrid.insertBefore(bannerContent, insertionPoint);
					insertionPoint = bannerContent.nextSibling;
				}
			}
		} finally {
			isRepositioning = false;
		}
	}

	function repositionCollectionBlocks(root = document) {
		const scope = root || document;

		// Info blocks first (grouped placement to prevent collisions).
		scheduleBannerPlacement(scope);

		// Copy blocks after info blocks have had a chance to move.
		window.requestAnimationFrame(() => {
			scope.querySelectorAll("collection-featured-copy-block").forEach((block) => {
				if (typeof block.placeCopyBlock === "function") block.placeCopyBlock();
			});
		});
	}

	if (!customElements.get("collection-grid-banner")) {
		class CollectionGridBanner extends HTMLElement {
			constructor() {
				super();
				this.bannerContent = this.querySelector("li");
			}

			connectedCallback() {
				// Initial placement.
				this.placeBanner();

				// Reposition on resize.
				this.resizeHandler = this.handleResize.bind(this);
				window.addEventListener("resize", this.resizeHandler);
			}

			disconnectedCallback() {
				window.removeEventListener("resize", this.resizeHandler);
			}

			handleResize() {
				this.placeBanner();
			}

			placeBanner() {
				// We do grouped placement across all banners to avoid collisions + MutationObserver loops.
				scheduleBannerPlacement(document);
			}
		}

		customElements.define("collection-grid-banner", CollectionGridBanner);
	}

	if (!customElements.get("collection-featured-copy-block")) {
		class CollectionFeaturedCopyBlock extends HTMLElement {
			constructor() {
				super();
				this.blockIndex = parseInt(this.dataset.blockIndex) || 0;
				this.copyBlockContent = this.querySelector("li");
				this.desktopCols = this.getDesktopCols();
			}

			connectedCallback() {
				// If a page fragment re-renders this block (eg. infinite scroll),
				// keep only the first copy block per index.
				if (this.copyBlockContent) {
					const matches = Array.from(
						document.querySelectorAll(`li[data-copy-block-index="${this.blockIndex}"]`)
					);
					if (matches.length > 1 && matches[0] !== this.copyBlockContent) {
						this.remove();
						return;
					}
				}

				// Defer placement by a tick so banners can move first.
				window.requestAnimationFrame(() => this.placeCopyBlock());

				this.resizeHandler = this.handleResize.bind(this);
				window.addEventListener("resize", this.resizeHandler);
			}

			disconnectedCallback() {
				window.removeEventListener("resize", this.resizeHandler);
			}

			handleResize() {
				this.desktopCols = this.getDesktopCols();
				this.placeCopyBlock();
			}

			getDesktopCols() {
				const productGrid = document.querySelector("#product-grid");
				if (productGrid && productGrid.dataset.desktopCols) {
					return parseInt(productGrid.dataset.desktopCols) || 4;
				}
				if (productGrid) {
					const classes = productGrid.className;
					const match = classes.match(/wt-grid--lg-cols-(\d+)/);
					if (match) return parseInt(match[1]) || 4;
				}
				const firstProduct = document.querySelector("#product-grid li[data-item-number]");
				if (firstProduct) {
					const classes = firstProduct.className;
					if (classes.includes("w-third")) return 3;
					if (classes.includes("w-25")) return 4;
					if (classes.includes("w-20")) return 5;
				}
				return 4;
			}

			getMobileCols() {
				const productGrid = document.querySelector("#product-grid");
				if (productGrid) {
					const classes = productGrid.className;
					const match = classes.match(/wt-grid--cols-(\d+)/);
					if (match) return parseInt(match[1]) || 2;
				}
				const firstProduct = document.querySelector("#product-grid li[data-item-number]");
				if (firstProduct) {
					const classes = firstProduct.className;
					if (classes.includes("w-50")) return 2;
					if (classes.includes("w-33")) return 3;
					if (classes.includes("w-25")) return 4;
				}
				return 2;
			}

			isMobile() {
				return window.innerWidth < 960;
			}

			placeCopyBlock() {
				if (!this.copyBlockContent) return;

				const productGrid = document.querySelector("#product-grid");
				if (!productGrid) return;

				// Show the copy block content (li) if it hasn't been moved yet.
				this.copyBlockContent.style.display = "";

				const isMobileView = this.isMobile();
				const productsPerRow = isMobileView ? this.getMobileCols() : this.desktopCols;
				const rowsPerBlock = 2; // every two rows
				const targetRows = this.blockIndex * rowsPerBlock;
				const allGridItems = Array.from(productGrid.children);

				if (this.blockIndex === 0) {
					const firstProduct = allGridItems.find((item) => {
						if (item === this.copyBlockContent || item.tagName !== "LI") return false;
						const isInfoBlock = item.querySelector(".productitem--info-block") !== null;
						const isCopyBlock =
							item.hasAttribute("data-copy-block-index") ||
							(item.classList.contains("col-span-full") &&
								item.querySelector(".featured-copy-block--title, .featured-copy-block--copy-dropcap") !== null);
						return !isInfoBlock && !isCopyBlock;
					});

					if (firstProduct && this.copyBlockContent.parentNode !== productGrid) {
						productGrid.insertBefore(this.copyBlockContent, firstProduct);
					} else if (!firstProduct && this.copyBlockContent.parentNode !== productGrid) {
						productGrid.appendChild(this.copyBlockContent);
					}
					return;
				}

				// Calculate position based on visual rows (info blocks consume full rows).
				let currentRow = 0;
				let productsInCurrentRow = 0;
				let insertionPoint = null;

				for (let i = 0; i < allGridItems.length; i++) {
					const item = allGridItems[i];

					if (item === this.copyBlockContent) continue;
					if (item.tagName !== "LI") continue;

					const isInfoBlock = item.querySelector(".productitem--info-block") !== null;
					const isCopyBlock =
						item.hasAttribute("data-copy-block-index") ||
						(item.classList.contains("col-span-full") &&
							(item.querySelector(".featured-copy-block--title, .featured-copy-block--copy-dropcap") !== null ||
								item.closest("collection-featured-copy-block") !== null));

					if (isInfoBlock) {
						if (productsInCurrentRow > 0) {
							currentRow++;
							productsInCurrentRow = 0;
						}
						currentRow++;
						productsInCurrentRow = 0;

						if (currentRow >= targetRows) {
							insertionPoint = item.nextSibling;
							break;
						}
					} else if (!isCopyBlock) {
						productsInCurrentRow++;

						if (productsInCurrentRow >= productsPerRow) {
							currentRow++;
							productsInCurrentRow = 0;

							if (currentRow >= targetRows) {
								insertionPoint = item.nextSibling;
								break;
							}
						}
					}
				}

				if (insertionPoint !== null && insertionPoint !== this.copyBlockContent) {
					productGrid.insertBefore(this.copyBlockContent, insertionPoint);
				} else if (this.copyBlockContent.parentNode !== productGrid) {
					productGrid.appendChild(this.copyBlockContent);
				}
			}
		}

		customElements.define("collection-featured-copy-block", CollectionFeaturedCopyBlock);
	}

	// Infinite scroll loader (used by collection-endless.liquid).
	// This is intentionally small and resilient: if it fails, the Next page link still works.
	if (!customElements.get("infinite-scroll")) {
		class InfiniteScroll extends HTMLElement {
			constructor() {
				super();
				this.productsGrid = null;
				this.loading = null;
				this.nextLink = null;
				this.observer = null;
				this.isLoading = false;
			}

			connectedCallback() {
				this.productsGrid = document.querySelector("#product-grid");
				if (!this.productsGrid) return;

				this.loading = this.querySelector(".loading-overlay");
				this.nextLink = this.querySelector("a[href]");

				// Hide the link when JS is active; we’ll re-show it on errors.
				if (this.nextLink) this.nextLink.classList.add("dn");

				if (this.dataset.lastPage === "true") return;
				if (!this.dataset.url) return;

				this.observer = new IntersectionObserver(
					(entries) => {
						const entry = entries && entries[0];
						if (!entry || !entry.isIntersecting) return;
						this.loadNextPage();
					},
					{ rootMargin: "0px 0px 600px 0px" }
				);
				this.observer.observe(this);
			}

			disconnectedCallback() {
				if (this.observer) this.observer.disconnect();
			}

			async loadNextPage() {
				if (this.isLoading) return;
				if (this.dataset.lastPage === "true") return;
				const url = this.dataset.url;
				if (!url) return;

				this.isLoading = true;
				if (this.loading) this.loading.classList.remove("dn");

				try {
					const response = await fetch(url);
					const text = await response.text();

					const html = document.createElement("div");
					html.innerHTML = text;

					const products = html.querySelector("#product-grid");
					const nextInfinite = html.querySelector("infinite-scroll");

					if (products && products.innerHTML.trim().length) {
						const existingItems = this.productsGrid.querySelectorAll("li[data-item-number]");
						const lastItemNumber =
							existingItems.length > 0
								? parseInt(existingItems[existingItems.length - 1].getAttribute("data-item-number")) || 0
								: 0;

						// Renumber items in the response before inserting, so each item number is unique.
						const newItems = products.querySelectorAll("li[data-item-number]");
						newItems.forEach((item, index) => {
							item.setAttribute("data-item-number", String(lastItemNumber + index + 1));
						});

						this.productsGrid.insertAdjacentHTML("beforeend", products.innerHTML);
					}

					if (nextInfinite && nextInfinite.dataset && nextInfinite.dataset.url) {
						this.dataset.url = nextInfinite.dataset.url;
					} else {
						this.dataset.lastPage = "true";
						if (this.observer) this.observer.disconnect();
						this.remove();
					}
				} catch (e) {
					// Fallback: allow manual paging.
					if (this.nextLink) this.nextLink.classList.remove("dn");
					console.error(e);
				} finally {
					if (this.loading) this.loading.classList.add("dn");
					this.isLoading = false;
				}
			}
		}

		customElements.define("infinite-scroll", InfiniteScroll);
	}

	// Initial positioning.
	document.addEventListener("DOMContentLoaded", () => {
		repositionCollectionBlocks(document);
	});

	// Theme editor: re-run positioning when Shopify re-renders the section.
	document.addEventListener("shopify:section:load", (event) => repositionCollectionBlocks(event.target));
	document.addEventListener("shopify:section:select", (event) => repositionCollectionBlocks(event.target));

	// Dynamic grid updates (infinite scroll inserts, etc.)
	let observer;
	function ensureObserver() {
		const grid = getProductGrid(document);
		if (!grid) return;
		if (observer) return;

		observer = new MutationObserver(() => {
			// Ignore mutations we caused ourselves (prevents reorder thrash).
			if (isRepositioning) return;
			repositionCollectionBlocks(document);
		});
		observer.observe(grid, { childList: true });
	}

	// Try immediately and again after DOM is ready.
	ensureObserver();
	document.addEventListener("DOMContentLoaded", ensureObserver);
})();

